import type { GridPaginationModel } from '@mui/x-data-grid-pro';
import { keepPreviousData, UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AxiosHeaders, RawAxiosRequestHeaders } from 'axios';
import type { PaginatedData } from '../@types/api';
import { ApiService, MethodsHeaders, getData, hasApiMorePages } from '../utils/api';

type Pagination = {
  page: number;
  limit: number;
};

export type AdditionalQueryParams = {
  [key: string]: unknown;
};

type QueryParams = Pagination & AdditionalQueryParams;
const EMPTY_EXTERNAL_QUERY_PARAMS: AdditionalQueryParams = {};

export const usePagination = <T, TTotals = T>(
  endPoint: string,
  initialQueryParams?: QueryParams,
  options?: Omit<
    UseQueryOptions<PaginatedData<T, TTotals>, unknown, PaginatedData<T, TTotals>, [string, QueryParams]>,
    'queryKey' | 'queryFn'
  >,
  apiService?: ApiService,
  headers?: (RawAxiosRequestHeaders & MethodsHeaders) | AxiosHeaders,
  externalQueryParams: AdditionalQueryParams = EMPTY_EXTERNAL_QUERY_PARAMS
) => {
  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    limit: 25,
    ...initialQueryParams,
  });

  const queryClient = useQueryClient();
  const requestQueryParams = useMemo(
    () => ({ ...queryParams, ...externalQueryParams }),
    [externalQueryParams, queryParams]
  );

  const updateQueryParams = (params: GridPaginationModel) => {
    return setQueryParams((prevValue) => {
      return {
        ...prevValue,
        limit: params.pageSize,
        // mui pagination starts from 0 and api starts from 1 so we need to add 1
        page: params.page + 1,
      };
    });
  };

  const changeQuery = useCallback(
    (query: AdditionalQueryParams) => {
      return setQueryParams((prevValue) => {
        const queryData: QueryParams = { ...prevValue, ...query, page: 1 };
        Object.keys(queryData).forEach((key) => {
          if (queryData[key] === null) {
            delete queryData[key];
          }
        });
        return queryData;
      });
    },
    [setQueryParams]
  );

  const { data, isFetching, isPending, refetch, status } = useQuery({
    queryKey: [endPoint, requestQueryParams],
    queryFn: (): Promise<PaginatedData<T, TTotals>> => getData(endPoint, requestQueryParams, apiService, headers),
    ...options,
    placeholderData: keepPreviousData,
  });

  const prefetchData = useCallback(
    (prefetchPage: number) => {
      queryClient.prefetchQuery({
        queryKey: [
          endPoint,
          {
            ...requestQueryParams,
            page: prefetchPage,
          },
        ],
        queryFn: () => getData(endPoint, { ...requestQueryParams, page: prefetchPage }, apiService),
      });
    },
    [queryClient, endPoint, requestQueryParams, apiService]
  );

  useEffect(() => {
    if (data && hasApiMorePages(data.count, queryParams.page, data.limit)) {
      prefetchData(queryParams.page + 1);
    }

    if ((queryParams?.page as number) > 1) {
      prefetchData(queryParams.page - 1);
    }
  }, [data, prefetchData, queryParams?.page]);

  return {
    data,
    activePage: queryParams.page,
    isFetching,
    isPending,
    isLoading: isPending,
    changeQuery,
    limit: queryParams.limit,
    updateQueryParams,
    refetch,
    status,
  };
};
